import { useForm, useStore } from "@tanstack/react-form";
import { FileText, Pencil, Plus, Power, Sparkles, Tag, Type } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RoutineRoleId } from "@/types/routineAreas.ts";
import { useLocation, useParams } from "wouter";

import {
  cardListClass,
  cardSurfaceClass,
  clickableCardClass,
  galleryCardsClass,
} from "../components/threads/shared.tsx";
import { Loader } from "../components/loader/Loader.tsx";
import {
  appliedFilterLabel,
  type PanelControlAxes,
  type PanelControlDimension,
  PanelControls,
  type PanelViewMode,
  viewAxis,
} from "./Settings/panelControls.tsx";
import { panelDescriptionClass, SettingsPanelHeader } from "./Settings/SettingsPanelHeader.tsx";
import { useSettingsUnsavedGuard } from "./Settings/settingsNavGuard.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog.tsx";
import { Button } from "../components/ui/button.tsx";
import { Input } from "../components/ui/input.tsx";
import { Label } from "../components/ui/label.tsx";
import { Textarea } from "../components/ui/textarea.tsx";
import { errorToast } from "../components/ui/toast.tsx";
import { Toggle } from "../components/ui/toggle.tsx";
import { cn } from "../lib/utils.ts";
import { useSkill, useSkillMutations, useSkills } from "../hooks/useSkills.ts";
import { useRoutinesV2 } from "../components/routines/useRoutinesV2.ts";
import {
  AreaRoleSection,
  areaRolePatch,
  knownRoles,
} from "@/components/routines/AreaRoleFields.tsx";
import {
  AreaPills,
  type AreaRoleFilter,
  areaRoleFilterAxes,
  SKILLS_AREA_ROLE_STORAGE_KEY,
  useAreaRoleFilter,
} from "@/components/routines/areaRoleFilter.tsx";
import { filterByAreaAndRole } from "@/shared/areaRoleFilter.ts";
import {
  RESERVED_SKILL_NAMES,
  SKILL_AMENDMENT_MAX,
  SKILL_BODY_MAX,
  SKILL_DESCRIPTION_MAX,
  SKILL_NAME_MAX,
  SKILL_NAME_MIN,
  SKILL_NAME_PATTERN,
  type SkillResp,
  toSkillNameSlug,
} from "../types/skills.ts";
import { CanvasPivotPanel } from "@/components/layout/SplitPanel/CanvasPivotPanel.tsx";
import { PanelToolbar } from "@/components/layout/SplitPanel/PanelToolbar.tsx";

// The skills flow lives inside the Settings screen (see `views/Settings`), so
// all in-flow navigation is relative to this base rather than a top-level
// `/skills` route.
export const SKILLS_BASE_PATH = "/settings/skills";

/**
 * What the composer is pre-filled with when a skill is started from here. Ends
 * mid-sentence, with the trailing space, so the cursor lands where the user
 * keeps typing.
 *
 * Exported so the canvas view seeds the same sentence this list does: one
 * prompt, so the two surfaces cannot start the user off differently.
 */
export const NEW_SKILL_PROMPT = "Build me a skill that ";

/**
 * In-tab page layout for the skill detail and create flows: the shared
 * settings header in child-screen mode, which renders the breadcrumb
 * (Skills › name) on desktop and pushes the title into the mobile chrome.
 * The list view uses `SettingsPanelHeader` directly, with its "New skill"
 * action.
 */
const SkillsPanel = ({
  title,
  onBack,

  children,
}: {
  title: string;
  onBack: () => void;

  children: ReactNode;
}) => (
  <div className="pb-12">
    <SettingsPanelHeader
      title={title}
      onBack={onBack}
      parentLabel="Skills"
      backStatsigName="skills-panel-back"
    />
    {children}
  </div>
);

// --- List row ---

const SkillRow = ({
  skill,
  view = "list",
  onOpen,
}: {
  skill: SkillResp;
  view?: PanelViewMode;
  /**
   * Open this skill in place. Without it the row navigates to the skill's own
   * settings route, which a canvas host would present as an overlay over
   * whatever it was already showing.
   */
  onOpen?: (name: string) => void;
}) => {
  const { setEnabled } = useSkillMutations();
  const [, setLocation] = useLocation();
  const [pending, setPending] = useState(false);

  const handleToggle = useCallback(
    async (next: boolean) => {
      setPending(true);
      try {
        await setEnabled(skill.name, next);
      } catch {
        errorToast(`Failed to ${next ? "enable" : "disable"} ${skill.name}`);
      } finally {
        setPending(false);
      }
    },
    [skill.name, setEnabled],
  );

  const handleRowClick = useCallback(() => {
    if (onOpen) {
      onOpen(skill.name);
      return;
    }
    setLocation(`${SKILLS_BASE_PATH}/${encodeURIComponent(skill.name)}`);
  }, [skill.name, setLocation, onOpen]);

  // Stop click propagation so toggling doesn't navigate to the detail.
  const toggle = (
    <div onClick={(e) => e.stopPropagation()}>
      {/* `md`: sized to sit level with the status pills, which `default`'s 44px
          track overwhelmed and `sm`'s 12px track sat under. */}
      <Toggle size="md" checked={skill.enabled} onCheckedChange={handleToggle} disabled={pending} />
    </div>
  );

  // Both views sit on the thread card's surface and hover reveal, so a
  // clickable card behaves the same here as in a thread list. Only the
  // arrangement differs: a row reads across, a card reads down.
  if (view === "card") {
    return (
      <div className={cn(clickableCardClass, "flex flex-col")} onClick={handleRowClick}>
        <div className="flex items-center gap-2">
          <span className="flex-1 min-w-0 font-medium text-sm truncate">{skill.name}</span>
          {toggle}
        </div>
        <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
          {skill.description || "No description"}
        </div>
      </div>
    );
  }

  return (
    <div className={cn(clickableCardClass, "flex items-center gap-4")} onClick={handleRowClick}>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm truncate mb-1">{skill.name}</div>
        <div className="text-xs text-muted-foreground">{skill.description || "No description"}</div>
      </div>
      {toggle}
    </div>
  );
};

/**
 * All / System / Custom, the same three words Routines filters by — the two
 * lists hold the same kind of thing and shouldn't name it differently.
 *
 * An `override` is a shipped skill the user has amended, so it counts as
 * System: its body is still maintained upstream, which is what the distinction
 * is about. `SkillDetail` draws the same line.
 */
const SKILL_FILTERS = [
  { value: "all", label: "All" },
  { value: "system", label: "System" },
  { value: "custom", label: "Custom" },
] as const;

type SkillFilter = (typeof SKILL_FILTERS)[number]["value"];

const isSkillFilter = (value: string): value is SkillFilter =>
  SKILL_FILTERS.some((option) => option.value === value);

const matchesSkillFilter = (skill: SkillResp, filter: SkillFilter) => {
  if (filter === "all") return true;
  const isSystem = skill.kind === "system" || skill.kind === "override";
  return filter === "system" ? isSystem : !isSystem;
};

// --- List view ---

/**
 * The list's own axes — search, the System / Custom filter, and card/list, and
 * under routines_v2 the area and roles — owned in one place so the settings
 * screen and the canvas view offer the same ones, spelled the same way.
 */
export const useSkillsListControls = () => {
  const [searchQuery, setSearchQuery] = useState("");
  // Cards by default: a skill is a name plus a description, which is what a
  // card is for. The reading lists (Library) open on rows instead.
  const [view, setView] = useState<PanelViewMode>("card");
  const [filter, setFilter] = useState<SkillFilter>("all");
  const routinesV2 = useRoutinesV2();
  const areaRole = useAreaRoleFilter({
    storageKey: SKILLS_AREA_ROLE_STORAGE_KEY,
    enabled: routinesV2.enabled,
    statsigName: "skills-role",
  });

  const kindAxis: PanelControlDimension = {
    label: "Filter",
    value: filter,
    onChange: (value) => {
      if (isSkillFilter(value)) setFilter(value);
    },
    options: SKILL_FILTERS,
    defaultValue: "all",
    display: "segmented",
    statsigName: "skills-filter",
  };

  const controls: PanelControlAxes = {
    ...areaRoleFilterAxes(kindAxis, areaRole),
    view: viewAxis({
      value: view,
      onChange: setView,
      defaultValue: "card",
      statsigName: "skills-view",
    }),
  };

  return {
    searchQuery,
    filter,
    view,
    controls,
    areaRole,
    search: {
      value: searchQuery,
      onChange: setSearchQuery,
      placeholder: "Search skills",
      statsigName: "skills-search",
    },
  };
};

/**
 * The results only: whoever is showing them owns the header that narrows them.
 * The settings screen pairs it with `SettingsPanelHeader`, the canvas with its
 * own panel — the same contract `RoutinesListBody` already has.
 */
export const SkillsListBody = ({
  searchQuery,
  filter,
  view,
  areaRole,
  onOpen,
}: {
  searchQuery: string;
  filter: SkillFilter;
  view: PanelViewMode;
  /** Set under `routines_v2`: the area pills and the Role filter narrow the list too. */
  areaRole?: AreaRoleFilter;
  /** Passed through to each row; see `SkillRow`'s own `onOpen`. */
  onOpen?: (name: string) => void;
}) => {
  const { skills, isLoading, error } = useSkills();

  const query = searchQuery.trim().toLowerCase();
  const {
    narrowed: narrowedSkills,
    visible: visibleSkills,
    isNarrowed: isNarrowedByAreaOrRole,
  } = filterByAreaAndRole(
    skills,
    areaRole?.selection,
    (skill) =>
      matchesSkillFilter(skill, filter) &&
      (!query || `${skill.name} ${skill.description ?? ""}`.toLowerCase().includes(query)),
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Sparkles className="size-8 mx-auto mb-3 opacity-50" />
        <div className="text-lg font-medium mb-2">Couldn't load skills</div>
        <div className="text-sm">Try refreshing the page.</div>
      </div>
    );
  }

  // Kept while the list is empty, so the pill that emptied it can be undone
  // from where it was picked.
  const pills =
    areaRole && skills.length > 0 ? (
      <AreaPills
        items={narrowedSkills}
        labelItems={skills}
        filter={areaRole}
        statsigName="skills-area"
      />
    ) : null;

  // The empty state sits outside the grid container, not in it: as a grid child
  // it lands in the first 18rem column and centers itself against that, not the
  // canvas.
  //
  // A narrowed list gets its own wording: telling someone with skills that none
  // are available reads as a bug, not a non-match. A filter narrows just as a
  // search does, so it takes the same treatment — only a list that is empty on
  // its own terms gets the account-wide guidance, which is the one case where
  // "author your own" is the useful next step. The area and roles count only
  // while their pills show, as on Routines.
  if (visibleSkills.length === 0) {
    return (
      <>
        {pills}
        <div className="text-center py-12 text-muted-foreground">
          <Sparkles className="size-8 mx-auto mb-3 opacity-50" />
          {query ? (
            <div className="text-lg font-medium mb-2">No skills match your search</div>
          ) : isNarrowedByAreaOrRole && pills ? (
            <>
              <div className="text-lg font-medium mb-2">No skills match these filters</div>
              <div className="text-sm">Pick "All" above, or change the roles under Filter.</div>
            </>
          ) : filter !== "all" ? (
            <div className="text-lg font-medium mb-2">
              No {SKILL_FILTERS.find((option) => option.value === filter)?.label.toLowerCase()}{" "}
              skills
            </div>
          ) : (
            <>
              <div className="text-lg font-medium mb-2">No skills available</div>
              <div className="text-sm">
                Click "New skill" to author your own, or check back once system skills are
                available.
              </div>
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      {pills}
      <div className={view === "card" ? galleryCardsClass : cardListClass}>
        {visibleSkills.map((skill) => (
          <SkillRow key={skill.name} skill={skill} view={view} onOpen={onOpen} />
        ))}
      </div>
    </>
  );
};

/** The Skills tab of the settings screen: its own header over the shared body. */
const SkillsList = () => {
  const [, setLocation] = useLocation();
  const { searchQuery, filter, view, controls, areaRole, search } = useSkillsListControls();

  // Hands off to the composer with the ask already in it, as Routines does: a
  // skill is described to the assistant rather than filled into a form. Routed
  // through home's `?input=` deep link because the composer is mounted by the
  // calendar and Settings is its own route.
  const handleNew = useCallback(() => {
    setLocation(`/?input=${encodeURIComponent(NEW_SKILL_PROMPT)}`);
  }, [setLocation]);

  return (
    <div className="pb-12">
      <SettingsPanelHeader
        title="Skills"
        search={search}
        filters={
          <PanelControls labeled {...controls} subject="skills" statsigName="skills-filter" />
        }
        mobileFilters={<PanelControls {...controls} subject="skills" statsigName="skills-filter" />}
        toolsKey={appliedFilterLabel(controls)}
        action={{ label: "New skill", icon: Plus, onClick: handleNew, statsigName: "skills-new" }}
      />
      <p className={panelDescriptionClass}>
        Reusable instructions that teach the assistant how to handle specific tasks.
      </p>
      <SkillsListBody searchQuery={searchQuery} filter={filter} view={view} areaRole={areaRole} />
    </div>
  );
};

// --- Detail / edit view ---

const SectionLabel = ({ icon: Icon, children }: { icon: typeof Sparkles; children: string }) => (
  <div className="flex items-center gap-2 mb-3">
    <Icon className="size-3.5 text-muted-foreground" />
    <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {children}
    </span>
  </div>
);

/**
 * The skill editor: description, body or amendment, and the enable / delete /
 * reset actions.
 *
 * Exported so the canvas can present a skill with the same component this screen
 * uses rather than a reduced copy that drifts from it. `embedded` drops only the
 * in-tab header bar, whose back button and title the canvas panel already
 * supplies; everything below it, dialogs included, is shared.
 *
 * `onLeave` is how an embedded host reclaims the transitions that would
 * otherwise navigate to the skills list — going back, and finishing a delete.
 * On the canvas that navigation would take the whole page with it.
 */
export const SkillDetail = ({
  name,
  embedded = false,
  onLeave,
}: {
  name: string;
  /** Render without the in-tab header bar, for a host supplying its own chrome. */
  embedded?: boolean;
  /** Leave this skill (back, or a completed delete). Defaults to the skills list. */
  onLeave?: () => void;
}) => {
  const { skill, isLoading, error } = useSkill(name);
  const { updateSkill, deleteSkill, setEnabled } = useSkillMutations();
  const routinesV2 = useRoutinesV2();
  const [, setLocation] = useLocation();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // A skill okthx ships: its body is maintained upstream and shown read-only,
  // and the user's changes ride in `amendment`. A skill the user wrote is
  // theirs outright, so the body is the editable field and there is no
  // amendment.
  const isSystemSkill = skill?.kind === "system" || skill?.kind === "override";

  const defaultValues = useMemo(
    () => ({
      description: skill?.description ?? "",
      body: skill?.body ?? "",
      amendment: skill?.amendment ?? "",
      area: skill?.area,
      roles: knownRoles(skill?.roles),
    }),
    [skill?.description, skill?.body, skill?.amendment, skill?.area, skill?.roles],
  );

  const seededAreaRoles = useRef<{ area?: string; roles: RoutineRoleId[] }>({ roles: [] });

  const form = useForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      if (!skill) return;
      setIsSaving(true);
      try {
        await updateSkill(skill.name, {
          description: value.description,
          ...(isSystemSkill ? { amendment: value.amendment } : { body: value.body }),
          // Against the values the form was reset to, not `defaultValues`: a
          // refetch during the edit may carry another device's newer area or
          // roles, which an unrelated save must not send back over.
          ...(isSystemSkill ? {} : areaRolePatch(seededAreaRoles.current, value)),
        });
        // Reset to what was just saved, not to `defaultValues` — the refetch it
        // triggers has not landed yet. Without this the form stays dirty after a
        // successful save: the footer keeps offering Save/Discard, the re-seed
        // effect below is gated on `!isDirty` so it can never clear it, and the
        // tab-switch guard warns about discarding changes that are already
        // stored. `Routines.tsx` clears its own `userEditedRef` for the same
        // reason.
        resetForm(value);
      } catch (err) {
        // Surface the server's explanatory message when the API returns one
        // (e.g., 404 on a race-deleted ent). Falls back to the axios message
        // string only when no structured body is present.
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          (err instanceof Error ? err.message : "Failed to save");
        errorToast(message);
      } finally {
        setIsSaving(false);
      }
    },
  });

  // Every reset goes through here, so the save compares with what the form
  // was last reset to.
  const resetForm = useCallback(
    (values: typeof defaultValues) => {
      seededAreaRoles.current = { area: values.area, roles: values.roles };
      form.reset(values);
    },
    [form],
  );

  // Reset form when the skill loads/changes. Skip when the form is dirty so
  // a background refetch (triggered by invalidateAll after Save / Reset)
  // doesn't silently clobber edits the user started typing in the brief
  // window between the mutation response and the refetched skill landing.
  useEffect(() => {
    if (skill && !form.state.isDirty) resetForm(defaultValues);
  }, [skill, form, defaultValues, resetForm]);

  // Guard unsaved edits against a Settings tab switch — the flow lives inside
  // the tab shell, so switching tabs would unmount this and silently drop them.
  const isDirty = useStore(form.store, (state) => state.isDirty);
  const { promptingLeave, confirmLeave, cancelLeave } = useSettingsUnsavedGuard(isDirty);

  // Replace rather than push: this is a back button, so it must collapse the
  // detail's history entry. Pushing the list on top would leave the detail one
  // browser-back away, and the header's back button would re-enter it.
  //
  // An embedded host has no history entry to collapse — it swapped what it was
  // rendering — so it hands back its own way out instead.
  const handleBack = useCallback(() => {
    if (onLeave) {
      onLeave();
      return;
    }
    setLocation(SKILLS_BASE_PATH, { replace: true });
  }, [setLocation, onLeave]);

  const confirmDelete = useCallback(async () => {
    if (!skill) return;
    const isReset = skill.kind === "override";
    setIsDeleting(true);
    // For Delete (user-kind), navigate before the await so the now-doomed
    // detail observer unmounts before invalidateAll refetches a 404 —
    // otherwise the page flashes "Couldn't load this skill" right before
    // the route change. For Reset (override), stay on the page; the URL
    // still resolves to the underlying system skill and the refetched
    // detail reseeds the form via the useEffect below.
    if (!isReset) {
      handleBack();
    }
    try {
      await deleteSkill(skill.name);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Action failed";
      errorToast(message);
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [skill, deleteSkill, handleBack]);

  const handleToggleEnabled = useCallback(async () => {
    if (!skill) return;
    try {
      await setEnabled(skill.name, !skill.enabled);
    } catch {
      errorToast(`Failed to ${skill.enabled ? "disable" : "enable"} ${skill.name}`);
    }
  }, [skill, setEnabled]);

  // Embedded, the host is already showing a header with this skill's name on it,
  // so the in-tab panel would be a second one.
  const panel = (title: string, body: ReactNode) =>
    embedded ? (
      body
    ) : (
      <SkillsPanel title={title} onBack={handleBack}>
        {body}
      </SkillsPanel>
    );

  if (isLoading) {
    return panel(
      name,
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>,
    );
  }

  if (error || !skill) {
    return panel(
      name,
      <div className="text-center py-12 text-muted-foreground">Couldn't load this skill.</div>,
    );
  }

  const canDelete = skill.kind !== "system";
  const deleteLabel = skill.kind === "override" ? "Reset to default" : "Delete";

  return panel(
    skill.name,
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="py-4 max-w-2xl space-y-6"
      >
        {/* Description */}
        <div>
          <SectionLabel icon={Type}>When should we use this skill?</SectionLabel>
          <form.Field
            name="description"
            validators={{
              onChange: ({ value }) =>
                !value
                  ? "Description is required"
                  : value.length > SKILL_DESCRIPTION_MAX
                    ? `Description must be ${SKILL_DESCRIPTION_MAX} characters or fewer`
                    : undefined,
            }}
          >
            {(field) => (
              <div className="flex flex-col gap-1">
                <Input
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="e.g. When I want to decline a meeting politely"
                  maxLength={SKILL_DESCRIPTION_MAX}
                />
                {field.state.meta.errors.length > 0 && (
                  <span className="text-xs text-destructive">
                    {field.state.meta.errors.join(", ")}
                  </span>
                )}
              </div>
            )}
          </form.Field>
        </div>

        <div className="h-px bg-border" />

        {/* Your instructions — the editable half of a skill okthx ships. */}
        {isSystemSkill && (
          <>
            <div>
              <SectionLabel icon={Pencil}>Your instructions</SectionLabel>
              <form.Field
                name="amendment"
                validators={{
                  onChange: ({ value }) =>
                    value.length > SKILL_AMENDMENT_MAX
                      ? `Your instructions must be ${SKILL_AMENDMENT_MAX.toLocaleString()} characters or fewer`
                      : undefined,
                }}
              >
                {(field) => (
                  <div className="flex flex-col gap-2">
                    <p className="text-xs text-muted-foreground">
                      Added to this skill every time it runs. Where these disagree with the skill
                      below, these win.
                    </p>
                    <Textarea
                      id={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      rows={6}
                      placeholder={"- Always offer a phone call as an alternative."}
                      className="font-mono text-sm"
                    />
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      {field.state.meta.errors.length > 0 ? (
                        <span className="text-destructive">
                          {field.state.meta.errors.join(", ")}
                        </span>
                      ) : (
                        <span />
                      )}
                      <span>
                        {field.state.value.length.toLocaleString()} /{" "}
                        {SKILL_AMENDMENT_MAX.toLocaleString()}
                      </span>
                    </div>
                  </div>
                )}
              </form.Field>
            </div>

            <div className="h-px bg-border" />
          </>
        )}

        {/* Body */}
        <div>
          <SectionLabel icon={FileText}>{isSystemSkill ? "The skill" : "Body"}</SectionLabel>
          {isSystemSkill ? (
            // Read-only on purpose: okthx maintains this text and keeps
            // improving it, and those improvements reach the user precisely
            // because nothing here takes a copy of it.
            <div className="flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">Written and kept up to date by Okthx.</p>
              <pre
                className={cn(
                  cardSurfaceClass,
                  "max-h-96 overflow-auto whitespace-pre-wrap font-mono text-sm text-muted-foreground",
                )}
              >
                {skill.body}
              </pre>
            </div>
          ) : (
            <form.Field
              name="body"
              validators={{
                onChange: ({ value }) =>
                  !value
                    ? "Body is required"
                    : value.length > SKILL_BODY_MAX
                      ? `Body must be ${SKILL_BODY_MAX.toLocaleString()} characters or fewer`
                      : undefined,
              }}
            >
              {(field) => (
                <div className="flex flex-col gap-2">
                  <Textarea
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    rows={20}
                    placeholder="The instructions the agent follows when this skill runs."
                    className="font-mono text-sm"
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    {field.state.meta.errors.length > 0 ? (
                      <span className="text-destructive">{field.state.meta.errors.join(", ")}</span>
                    ) : (
                      <span />
                    )}
                    <span>
                      {field.state.value.length.toLocaleString()} /{" "}
                      {SKILL_BODY_MAX.toLocaleString()}
                    </span>
                  </div>
                </div>
              )}
            </form.Field>
          )}
        </div>

        {/* A skill the user wrote, under routines_v2. A shipped skill's area and
            roles are okthx's, like its body. */}
        {!isSystemSkill && routinesV2.enabled && (
          <>
            <div className="h-px bg-border" />
            <form.Field name="area">
              {(areaField) => (
                <form.Field name="roles">
                  {(rolesField) => (
                    <AreaRoleSection
                      area={areaField.state.value}
                      onAreaChange={areaField.handleChange}
                      roles={rolesField.state.value}
                      onRolesChange={rolesField.handleChange}
                      statsigPrefix="skills-detail"
                    />
                  )}
                </form.Field>
              )}
            </form.Field>
          </>
        )}

        <div className="h-px bg-border" />

        {/* Status — the same switch RoutineDetail uses, rather than one drawn
            by hand inside a native button. The text is the switch's label, so
            a click anywhere on the row still toggles it, without nesting one
            control inside another. */}
        <div>
          <SectionLabel icon={Power}>Status</SectionLabel>
          <div className={cn(cardSurfaceClass, "flex w-full items-center justify-between")}>
            {/* `font-normal leading-normal` undo the primitive's single-line
                defaults: this label is two lines, and each sets its own face. */}
            <Label
              htmlFor="skill-enabled"
              className="cursor-pointer text-left font-normal leading-normal"
            >
              <div className="text-sm font-medium">{skill.enabled ? "Enabled" : "Disabled"}</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {skill.enabled
                  ? "This skill is in the catalog and slash menu."
                  : "This skill is hidden from the catalog and slash menu."}
              </div>
            </Label>
            <span data-statsig-name="skills-detail-toggle-enabled">
              <Toggle
                id="skill-enabled"
                size="md"
                checked={skill.enabled}
                onCheckedChange={handleToggleEnabled}
              />
            </span>
          </div>
        </div>

        {/* Floating action bar — Save when dirty, Reset/Delete on the left. */}
        <form.Subscribe selector={(state) => [state.isDirty, state.canSubmit, state.isSubmitting]}>
          {([isDirty, canSubmit, isSubmitting]) => (
            <div
              className={cn(
                "flex items-center justify-end gap-2 p-3 rounded-lg border",
                isDirty ? "bg-muted/50 border-border" : "border-transparent",
              )}
            >
              {isDirty && (
                <>
                  <span className="text-xs text-muted-foreground mr-auto">
                    You have unsaved changes
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => resetForm(defaultValues)}
                    className="text-xs"
                    data-statsig-name="skills-detail-discard"
                  >
                    Discard
                  </Button>
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!canSubmit || isSubmitting || isSaving}
                    className="text-xs"
                    data-statsig-name="skills-detail-save"
                  >
                    {isSaving ? "Saving..." : "Save"}
                  </Button>
                </>
              )}
              {canDelete && (
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  disabled={isDeleting}
                  className="text-xs"
                  data-statsig-name="skills-detail-delete"
                >
                  {deleteLabel}
                </Button>
              )}
            </div>
          )}
        </form.Subscribe>
      </form>

      {canDelete && (
        <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {skill.kind === "override"
                  ? `Reset "${skill.name}" to its system default?`
                  : `Delete "${skill.name}"?`}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {skill.kind === "override"
                  ? "Your instructions for this skill will be discarded, and it goes back to how Okthx ships it."
                  : "This can't be undone. The skill will be removed from the catalog and the slash menu."}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmDelete}
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isDeleting ? `${deleteLabel}…` : deleteLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}

      <AlertDialog open={promptingLeave} onOpenChange={(open) => !open && cancelLeave()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Leaving this tab will discard them.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelLeave}>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLeave}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>,
  );
};

// --- New skill form ---

/**
 * The create flow. Takes the same `embedded` / `onLeave` pair `SkillDetail`
 * does, so a host supplying its own chrome — the canvas view's dialog — gets the
 * form and its actions without this panel's header, and cancelling returns to
 * whatever that host was showing instead of navigating away from it.
 */
export const NewSkillForm = ({
  embedded = false,
  onLeave,
}: {
  /** Render without the in-tab header bar, for a host supplying its own. */
  embedded?: boolean;
  /** Leave the form (cancel, or a completed create). Defaults to the list. */
  onLeave?: () => void;
} = {}) => {
  const { createUserSkill } = useSkillMutations();
  const [, setLocation] = useLocation();
  const [isSaving, setIsSaving] = useState(false);

  const form = useForm({
    defaultValues: { name: "", description: "", body: "" },
    onSubmit: async ({ value }) => {
      setIsSaving(true);
      try {
        const slug = toSkillNameSlug(value.name);
        const created = await createUserSkill({
          name: slug,
          description: value.description,
          body: value.body,
        });
        if (onLeave) {
          // An embedded host owns where the form sits, so it decides what
          // replaces it — navigating from here would take its whole page.
          onLeave();
        } else {
          // Replace so browser-back skips the new-skill form and lands on the list.
          setLocation(`${SKILLS_BASE_PATH}/${encodeURIComponent(created.name)}`, { replace: true });
        }
      } catch (err) {
        // The handler returns 409 with `{ error, reason }` on name collisions
        // or cap reached. Surface the server message rather than a generic.
        const message =
          (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          (err instanceof Error ? err.message : "Failed to create skill");
        errorToast(message);
      } finally {
        setIsSaving(false);
      }
    },
  });

  // Guard the draft against a Settings tab switch (the form lives inside the
  // tab shell, so a switch would unmount it and drop the draft).
  const isDirty = useStore(form.store, (state) => state.isDirty);
  const { promptingLeave, confirmLeave, cancelLeave } = useSettingsUnsavedGuard(isDirty);

  // Replace, so backing out of the create form doesn't leave it one
  // browser-back away — see the matching note in `SkillDetail`.
  const handleCancel = useCallback(() => {
    if (onLeave) {
      onLeave();
      return;
    }
    setLocation(SKILLS_BASE_PATH, { replace: true });
  }, [setLocation, onLeave]);

  const body = (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
        className="max-w-2xl space-y-6"
      >
        {/* Name — user types natural text, we slug behind the scenes. */}
        <div>
          <SectionLabel icon={Tag}>Name</SectionLabel>
          <form.Field
            name="name"
            validators={{
              // Validate against the slugged form so the user sees errors
              // tied to what'll actually be sent. We still accept a longer
              // raw input (to give the user room before the slug trims).
              onChange: ({ value }) => {
                const slug = toSkillNameSlug(value);
                if (!slug) return "Name is required";
                if (slug.length < SKILL_NAME_MIN)
                  return `Name must be at least ${SKILL_NAME_MIN} characters`;
                if (slug.length > SKILL_NAME_MAX)
                  return `Name must be ${SKILL_NAME_MAX} characters or fewer`;
                if (!SKILL_NAME_PATTERN.test(slug))
                  return "Name needs at least one letter or digit";
                if (RESERVED_SKILL_NAMES.has(slug)) return "This name is reserved — pick another.";
                return undefined;
              },
            }}
          >
            {(field) => {
              const slug = toSkillNameSlug(field.state.value);
              return (
                <div className="flex flex-col gap-1">
                  <Input
                    id={field.name}
                    value={field.state.value}
                    onChange={(e) => field.handleChange(e.target.value)}
                    onBlur={field.handleBlur}
                    placeholder="Clear Inbox"
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    {field.state.meta.errors.length > 0 ? (
                      <span className="text-destructive">{field.state.meta.errors.join(", ")}</span>
                    ) : (
                      <span>
                        Slash command: <span className="font-mono">/{slug || "name"}</span>
                      </span>
                    )}
                  </div>
                </div>
              );
            }}
          </form.Field>
        </div>

        <div className="h-px bg-border" />

        {/* Description */}
        <div>
          <SectionLabel icon={Type}>When should we use this skill?</SectionLabel>
          <form.Field
            name="description"
            validators={{
              onChange: ({ value }) =>
                !value
                  ? "Description is required"
                  : value.length > SKILL_DESCRIPTION_MAX
                    ? `Description must be ${SKILL_DESCRIPTION_MAX} characters or fewer`
                    : undefined,
            }}
          >
            {(field) => (
              <div className="flex flex-col gap-1">
                <Input
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  placeholder="e.g. When I want to decline a meeting politely"
                  maxLength={SKILL_DESCRIPTION_MAX}
                />
                {field.state.meta.errors.length > 0 && (
                  <span className="text-xs text-destructive">
                    {field.state.meta.errors.join(", ")}
                  </span>
                )}
              </div>
            )}
          </form.Field>
        </div>

        <div className="h-px bg-border" />

        {/* Body */}
        <div>
          <SectionLabel icon={FileText}>Body</SectionLabel>
          <form.Field
            name="body"
            validators={{
              onChange: ({ value }) =>
                !value
                  ? "Body is required"
                  : value.length > SKILL_BODY_MAX
                    ? `Body must be ${SKILL_BODY_MAX.toLocaleString()} characters or fewer`
                    : undefined,
            }}
          >
            {(field) => (
              <div className="flex flex-col gap-2">
                <Textarea
                  id={field.name}
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  rows={20}
                  placeholder="The instructions the agent follows when this skill runs."
                  className="font-mono text-sm"
                />
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  {field.state.meta.errors.length > 0 ? (
                    <span className="text-destructive">{field.state.meta.errors.join(", ")}</span>
                  ) : (
                    <span />
                  )}
                  <span>
                    {field.state.value.length.toLocaleString()} / {SKILL_BODY_MAX.toLocaleString()}
                  </span>
                </div>
              </div>
            )}
          </form.Field>
        </div>

        {/* Action bar — Create + Cancel */}
        <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
          {([canSubmit, isSubmitting]) => (
            <div className="flex items-center justify-end gap-2 p-3 rounded-lg bg-muted/50 border border-border">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="text-xs"
                data-statsig-name="skills-new-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={!canSubmit || isSubmitting || isSaving}
                className="text-xs"
                data-statsig-name="skills-new-create"
              >
                {isSaving ? "Creating..." : "Create skill"}
              </Button>
            </div>
          )}
        </form.Subscribe>
      </form>

      <AlertDialog open={promptingLeave} onOpenChange={(open) => !open && cancelLeave()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard new skill?</AlertDialogTitle>
            <AlertDialogDescription>
              You have an unsaved draft. Leaving this tab will discard it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelLeave}>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmLeave}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );

  return embedded ? (
    body
  ) : (
    <SkillsPanel title="New skill" onBack={handleCancel}>
      {body}
    </SkillsPanel>
  );
};

// --- Route entry ---

/**
 * Mode switcher for the skills flow, rendered inside the Settings "Skills" tab.
 * Reads the `:subId` segment of the nested Settings route: `"new"` → create
 * form, a concrete name → detail/edit, absent → list.
 */
export const SkillsFlow = () => {
  const params = useParams<{ subId?: string }>();
  const skillId = params.subId;
  if (skillId === "new") {
    return <NewSkillForm />;
  }
  if (skillId) {
    return <SkillDetail name={decodeURIComponent(skillId)} />;
  }
  return <SkillsList />;
};

/**
 * Skills on the canvas: the instructions the assistant follows for particular
 * tasks, with the same search, filter, and card/list tools the settings screen
 * offers.
 *
 * Built like `RoutinesCanvasView`, which holds the same shape of thing. Opening
 * a skill drills in place rather than navigating to its settings route, which
 * this page's `CanvasNavBoundary` would present as an overlay over whatever the
 * canvas was already showing.
 *
 * "New skill" hands off to the composer with the ask already in it, as
 * "New routine" does: a skill is described to the assistant rather than filled
 * into a form.
 */
export const SkillsCanvasView = ({
  accountSwitcher,
  leading,
  onSeedNewChat,
  openSkillName,
  onOpenSkill,
}: {
  accountSwitcher?: ReactNode;
  leading?: ReactNode;
  /**
   * Start a new chat with this text already in the composer. The page owns both
   * the seed and where a new chat opens, so it hands the capability down and
   * this view supplies only the sentence.
   */
  onSeedNewChat: (text: string) => void;
  /** The skill the URL names, or null for the list. Already decoded. */
  openSkillName: string | null;
  /**
   * Drill into a skill, or leave one with null. The page owns the address, so
   * it navigates and hands the result back down.
   */
  onOpenSkill: (name: string | null) => void;
}) => {
  const { searchQuery, filter, view, controls, areaRole, search } = useSkillsListControls();

  // Drilled in: `SkillDetail` already knows how to render without its own header
  // bar for a host supplying chrome, which is exactly this.
  if (openSkillName) {
    return (
      <CanvasPivotPanel
        title={openSkillName}
        parentLabel="Skills"
        onBack={() => onOpenSkill(null)}
        backStatsigName="skills-canvas-back"
        leading={leading}
        accountSwitcher={accountSwitcher}
      >
        <SkillDetail name={openSkillName} embedded onLeave={() => onOpenSkill(null)} />
      </CanvasPivotPanel>
    );
  }

  return (
    <CanvasPivotPanel
      title="Skills"
      leading={leading}
      accountSwitcher={accountSwitcher}
      toolbar={
        <PanelToolbar
          description="Reusable instructions that teach the assistant how to handle specific tasks."
          controls={controls}
          search={{ ...search, statsigName: "skills-canvas-search" }}
          subject="skills"
          statsigName="skills-canvas-filter"
          action={{
            label: "New skill",
            icon: Plus,
            onClick: () => onSeedNewChat(NEW_SKILL_PROMPT),
            statsigName: "skills-canvas-new",
          }}
        />
      }
    >
      <SkillsListBody
        searchQuery={searchQuery}
        filter={filter}
        view={view}
        areaRole={areaRole}
        onOpen={onOpenSkill}
      />
    </CanvasPivotPanel>
  );
};
